import { getApiError } from '../lib/apiError';
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Plus, Search, Truck, FileText, Package, Trash2, Eye, 
  CheckCircle, XCircle, Loader2, Calendar, Hash, Upload, FileUp, DollarSign, AlertTriangle, TrendingDown, ChevronDown, ChevronUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Purchases = () => {
  const [purchases, setPurchases] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [slowStockAlerts, setSlowStockAlerts] = useState([]);
  const [showSlowStockDetails, setShowSlowStockDetails] = useState(true);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    supplier_id: '',
    supplier_name: '',
    supplier_cnpj: '',
    invoice_number: '',
    invoice_series: '',
    invoice_key: '',
    invoice_date: '',
    entry_date: '',
    cfop: '',
    items: [],
    subtotal: 0,
    discount: 0,
    shipping: 0,
    other_costs: 0,
    total: 0,
    icms_total: 0,
    ipi_total: 0,
    pis_total: 0,
    cofins_total: 0,
    notes: ''
  });

  const [currentItem, setCurrentItem] = useState({
    product_id: '',
    product_name: '',
    sku: '',
    quantity: 1,
    unit_price: 0,
    total: 0,
    cfop: '',
    ncm: ''
  });

  useEffect(() => {
    fetchData();
  }, [filterStatus, filterSearch, filterStartDate, filterEndDate]);

  const fetchData = async () => {
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterSearch) params.search = filterSearch;
      if (filterStartDate) params.start_date = filterStartDate;
      if (filterEndDate) params.end_date = filterEndDate;
      const [purchasesRes, suppliersRes, productsRes, alertsRes] = await Promise.all([
        axios.get(`${API_URL}/api/purchases`, { params }),
        axios.get(`${API_URL}/api/suppliers`),
        axios.get(`${API_URL}/api/products`),
        axios.get(`${API_URL}/api/purchases/slow-stock-alerts`)
      ]);
      setPurchases(purchasesRes.data);
      setSuppliers(suppliersRes.data);
      const pData = productsRes.data;
      setProducts(Array.isArray(pData) ? pData : pData.products || []);
      setSlowStockAlerts(alertsRes.data.alerts || []);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      supplier_id: '', supplier_name: '', supplier_cnpj: '',
      invoice_number: '', invoice_series: '', invoice_key: '',
      invoice_date: '', entry_date: '', cfop: '',
      items: [], subtotal: 0, discount: 0, shipping: 0, other_costs: 0, total: 0,
      icms_total: 0, ipi_total: 0, pis_total: 0, cofins_total: 0, notes: ''
    });
    setCurrentItem({
      product_id: '', product_name: '', sku: '',
      quantity: 1, unit_price: 0, total: 0, cfop: '', ncm: ''
    });
  };

  const handleSupplierChange = (supplierId) => {
    const supplier = suppliers.find(s => s.id === supplierId);
    if (supplier) {
      setFormData({
        ...formData,
        supplier_id: supplierId,
        supplier_name: supplier.name,
        supplier_cnpj: supplier.cnpj || ''
      });
    }
  };

  const handleProductChange = (productId) => {
    const product = products.find(p => p.id === productId);
    if (product) {
      const total = product.price * currentItem.quantity;
      setCurrentItem({
        ...currentItem,
        product_id: productId,
        product_name: product.name,
        sku: product.sku || '',
        unit_price: product.cost_price || product.price,
        total: (product.cost_price || product.price) * currentItem.quantity
      });
    }
  };

  const handleQuantityChange = (qty) => {
    const quantity = parseInt(qty) || 1;
    setCurrentItem({
      ...currentItem,
      quantity,
      total: currentItem.unit_price * quantity
    });
  };

  const handlePriceChange = (price) => {
    const unit_price = parseFloat(price) || 0;
    setCurrentItem({
      ...currentItem,
      unit_price,
      total: unit_price * currentItem.quantity
    });
  };

  const addItem = () => {
    if (!currentItem.product_name || currentItem.quantity < 1) {
      toast.error('Preencha o produto e quantidade');
      return;
    }
    const newItems = [...formData.items, currentItem];
    const subtotal = newItems.reduce((sum, item) => sum + item.total, 0);
    const total = subtotal - formData.discount + formData.shipping + formData.other_costs;
    
    setFormData({
      ...formData,
      items: newItems,
      subtotal,
      total
    });
    setCurrentItem({
      product_id: '', product_name: '', sku: '',
      quantity: 1, unit_price: 0, total: 0, cfop: '', ncm: ''
    });
  };

  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    const subtotal = newItems.reduce((sum, item) => sum + item.total, 0);
    const total = subtotal - formData.discount + formData.shipping + formData.other_costs;
    
    setFormData({
      ...formData,
      items: newItems,
      subtotal,
      total
    });
  };

  const updateTotals = (field, value) => {
    const numValue = parseFloat(value) || 0;
    const newData = { ...formData, [field]: numValue };
    newData.total = newData.subtotal - newData.discount + newData.shipping + newData.other_costs;
    setFormData(newData);
  };

  const handleXmlImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.xml')) {
      toast.error('Selecione um arquivo XML válido');
      return;
    }

    setImporting(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API_URL}/api/purchases/import-xml`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const data = response.data;
      
      // Preenche o formulário com os dados do XML
      setFormData({
        supplier_id: data.supplier_id || '',
        supplier_name: data.supplier_name || '',
        supplier_cnpj: data.supplier_cnpj || '',
        invoice_number: data.invoice_number || '',
        invoice_series: data.invoice_series || '',
        invoice_key: data.invoice_key || '',
        invoice_date: data.invoice_date || '',
        entry_date: new Date().toISOString().split('T')[0],
        cfop: data.cfop || '',
        items: data.items || [],
        subtotal: data.subtotal || 0,
        discount: data.discount || 0,
        shipping: data.shipping || 0,
        other_costs: data.other_costs || 0,
        total: data.total || 0,
        icms_total: data.icms_total || 0,
        ipi_total: data.ipi_total || 0,
        pis_total: data.pis_total || 0,
        cofins_total: data.cofins_total || 0,
        notes: ''
      });

      toast.success(`XML importado com sucesso! ${data.items?.length || 0} itens encontrados.`);
      setDialogOpen(true);
    } catch (error) {
      toast.error(getApiError(error, 'Erro ao importar XML'));
    } finally {
      setImporting(false);
      // Limpa o input para permitir selecionar o mesmo arquivo novamente
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.supplier_name || !formData.invoice_number || formData.items.length === 0) {
      toast.error('Preencha fornecedor, número da NF e adicione itens');
      return;
    }

    // Se não tem supplier_id mas tem supplier_name, permite salvar assim mesmo
    const dataToSave = {
      ...formData,
      supplier_id: formData.supplier_id || 'manual'
    };

    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/purchases`, dataToSave);
      toast.success('Entrada registrada com sucesso!');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(getApiError(error, 'Erro ao registrar entrada'));
    } finally {
      setSaving(false);
    }
  };

  const confirmPurchase = async (id) => {
    if (!window.confirm('Confirmar entrada? Os produtos serão criados/atualizados e o estoque atualizado.')) return;
    
    try {
      const response = await axios.put(`${API_URL}/api/purchases/${id}/confirm`);
      const msg = response.data?.message || 'Entrada confirmada e estoque atualizado!';
      toast.success(msg);
      fetchData();
    } catch (error) {
      toast.error(getApiError(error, 'Erro ao confirmar entrada'));
    }
  };

  const cancelPurchase = async (id) => {
    if (!window.confirm('Cancelar esta entrada?')) return;
    
    try {
      await axios.put(`${API_URL}/api/purchases/${id}/cancel`);
      toast.success('Entrada cancelada!');
      fetchData();
    } catch (error) {
      toast.error(getApiError(error, 'Erro ao cancelar entrada'));
    }
  };

  const deletePurchase = async (id) => {
    const purchase = purchases.find(p => p.id === id);
    const isConfirmed = purchase?.status === 'confirmada';
    const msg = isConfirmed 
      ? `Excluir entrada NF ${purchase?.invoice_number}? O estoque dos produtos será REVERTIDO (subtraído).`
      : `Excluir entrada NF ${purchase?.invoice_number || ''}?`;
    if (!window.confirm(msg)) return;
    
    try {
      const response = await axios.delete(`${API_URL}/api/purchases/${id}`);
      toast.success(response.data?.message || 'Entrada excluída!');
      fetchData();
    } catch (error) {
      toast.error(getApiError(error, 'Erro ao excluir entrada'));
    }
  };

  const viewPurchase = (purchase) => {
    setSelectedPurchase(purchase);
    setViewDialogOpen(true);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  const statusColors = {
    pendente: 'bg-amber-100 text-amber-800',
    confirmada: 'bg-emerald-100 text-emerald-800',
    cancelada: 'bg-red-100 text-red-800'
  };

  const statusLabels = {
    pendente: 'Pendente',
    confirmada: 'Confirmada',
    cancelada: 'Cancelada'
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="purchases-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Entrada de Mercadorias</h1>
          <p className="text-slate-500 mt-1">Registre notas fiscais de entrada</p>
        </div>
        <div className="flex gap-2">
          {/* Botão Importar XML */}
          <input
            type="file"
            ref={fileInputRef}
            accept=".xml"
            onChange={handleXmlImport}
            className="hidden"
            data-testid="xml-file-input"
          />
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            className="border-emerald-600 text-emerald-600 hover:bg-emerald-50"
            data-testid="import-xml-btn"
          >
            {importing ? (
              <Loader2 className="animate-spin mr-2" size={18} />
            ) : (
              <FileUp size={18} className="mr-2" />
            )}
            Importar XML
          </Button>
          
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-purchase-btn">
                <Plus size={18} className="mr-2" />
                Nova Entrada
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Registrar Entrada de Mercadorias</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              <Tabs defaultValue="dados" className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="dados">Dados da NF</TabsTrigger>
                  <TabsTrigger value="itens">Itens</TabsTrigger>
                  <TabsTrigger value="impostos">Impostos e Totais</TabsTrigger>
                </TabsList>

                {/* Tab Dados da NF */}
                <TabsContent value="dados" className="space-y-4 mt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Fornecedor *</Label>
                      <Select value={formData.supplier_id} onValueChange={handleSupplierChange}>
                        <SelectTrigger data-testid="purchase-supplier">
                          <SelectValue placeholder="Selecione o fornecedor" />
                        </SelectTrigger>
                        <SelectContent>
                          {suppliers.map((s) => (
                            <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>CNPJ do Fornecedor</Label>
                      <Input
                        value={formData.supplier_cnpj}
                        onChange={(e) => setFormData({ ...formData, supplier_cnpj: e.target.value })}
                        placeholder="00.000.000/0000-00"
                        data-testid="purchase-cnpj"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Número da NF *</Label>
                      <Input
                        value={formData.invoice_number}
                        onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                        placeholder="000000"
                        data-testid="purchase-nf-number"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Série</Label>
                      <Input
                        value={formData.invoice_series}
                        onChange={(e) => setFormData({ ...formData, invoice_series: e.target.value })}
                        placeholder="1"
                        data-testid="purchase-nf-series"
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label>Chave de Acesso NF-e (44 dígitos)</Label>
                      <Input
                        value={formData.invoice_key}
                        onChange={(e) => setFormData({ ...formData, invoice_key: e.target.value })}
                        placeholder="00000000000000000000000000000000000000000000"
                        maxLength={44}
                        className="font-mono text-sm"
                        data-testid="purchase-nf-key"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Data de Emissão *</Label>
                      <Input
                        type="date"
                        value={formData.invoice_date}
                        onChange={(e) => setFormData({ ...formData, invoice_date: e.target.value })}
                        data-testid="purchase-invoice-date"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Data de Entrada</Label>
                      <Input
                        type="date"
                        value={formData.entry_date}
                        onChange={(e) => setFormData({ ...formData, entry_date: e.target.value })}
                        data-testid="purchase-entry-date"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>CFOP Principal</Label>
                      <Input
                        value={formData.cfop}
                        onChange={(e) => setFormData({ ...formData, cfop: e.target.value })}
                        placeholder="1102"
                        data-testid="purchase-cfop"
                      />
                    </div>
                  </div>
                </TabsContent>

                {/* Tab Itens */}
                <TabsContent value="itens" className="space-y-4 mt-4">
                  {/* Add Item Form */}
                  <Card className="border border-slate-200">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium">Adicionar Item</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                        <div className="md:col-span-2">
                          <Select value={currentItem.product_id || "new"} onValueChange={(v) => v === "new" ? null : handleProductChange(v)}>
                            <SelectTrigger>
                              <SelectValue placeholder="Produto cadastrado" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="new">Novo produto</SelectItem>
                              {products.map((p) => (
                                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Input
                            value={currentItem.product_name}
                            onChange={(e) => setCurrentItem({ ...currentItem, product_name: e.target.value })}
                            placeholder="Nome"
                          />
                        </div>
                        <div>
                          <Input
                            type="number"
                            min="1"
                            value={currentItem.quantity}
                            onChange={(e) => handleQuantityChange(e.target.value)}
                            placeholder="Qtd"
                          />
                        </div>
                        <div>
                          <Input
                            type="number"
                            step="0.01"
                            value={currentItem.unit_price}
                            onChange={(e) => handlePriceChange(e.target.value)}
                            placeholder="Valor Unit."
                          />
                        </div>
                        <div>
                          <Button type="button" onClick={addItem} className="w-full bg-emerald-600 hover:bg-emerald-700">
                            Adicionar
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Items List */}
                  {formData.items.length > 0 && (
                    <Card className="border border-slate-200">
                      <CardContent className="p-0">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Produto</TableHead>
                              <TableHead>SKU</TableHead>
                              <TableHead className="text-center">Qtd</TableHead>
                              <TableHead className="text-right">Unit.</TableHead>
                              <TableHead className="text-right">Total</TableHead>
                              <TableHead></TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {formData.items.map((item, index) => (
                              <TableRow key={index}>
                                <TableCell className="font-medium">{item.product_name}</TableCell>
                                <TableCell className="font-mono text-sm">{item.sku || '-'}</TableCell>
                                <TableCell className="text-center">{item.quantity}</TableCell>
                                <TableCell className="text-right">{formatCurrency(item.unit_price)}</TableCell>
                                <TableCell className="text-right font-medium">{formatCurrency(item.total)}</TableCell>
                                <TableCell>
                                  <Button variant="ghost" size="sm" onClick={() => removeItem(index)} className="text-red-600">
                                    <Trash2 size={16} />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>

                {/* Tab Impostos */}
                <TabsContent value="impostos" className="space-y-4 mt-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Subtotal</Label>
                      <Input value={formatCurrency(formData.subtotal)} readOnly className="bg-slate-50" />
                    </div>
                    <div className="space-y-2">
                      <Label>Desconto</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.discount}
                        onChange={(e) => updateTotals('discount', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Frete</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.shipping}
                        onChange={(e) => updateTotals('shipping', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Outras Despesas</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.other_costs}
                        onChange={(e) => updateTotals('other_costs', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Total ICMS</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.icms_total}
                        onChange={(e) => setFormData({ ...formData, icms_total: parseFloat(e.target.value) || 0 })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Total IPI</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.ipi_total}
                        onChange={(e) => setFormData({ ...formData, ipi_total: parseFloat(e.target.value) || 0 })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Total PIS</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.pis_total}
                        onChange={(e) => setFormData({ ...formData, pis_total: parseFloat(e.target.value) || 0 })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Total COFINS</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.cofins_total}
                        onChange={(e) => setFormData({ ...formData, cofins_total: parseFloat(e.target.value) || 0 })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Total da NF</Label>
                      <Input value={formatCurrency(formData.total)} readOnly className="bg-slate-50 font-bold text-lg" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Observações</Label>
                    <Input
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Observações da entrada"
                    />
                  </div>
                </TabsContent>
              </Tabs>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={saving}>
                  {saving ? <Loader2 className="animate-spin mr-2" size={18} /> : null}
                  Registrar Entrada
                </Button>
              </div>
            </form>
          </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Aviso de Mercadorias Paradas */}
      {slowStockAlerts.length > 0 && (
        <Card className="border-l-4 border-l-amber-500 border border-amber-200 bg-amber-50/50" data-testid="slow-stock-alert-card">
          <CardContent className="p-4">
            <div
              className="flex items-center justify-between cursor-pointer"
              onClick={() => setShowSlowStockDetails(!showSlowStockDetails)}
              data-testid="slow-stock-alert-header"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <AlertTriangle className="text-amber-600" size={22} />
                </div>
                <div>
                  <p className="font-semibold text-amber-800">
                    {slowStockAlerts.length} mercadoria(s) parada(s) precisam de remarcação de preço
                  </p>
                  <p className="text-xs text-amber-700">
                    Entradas com 180+ dias e 50% ou mais do estoque ainda na loja — 180 dias: baixar 20% | 365 dias: baixar 30%
                  </p>
                </div>
              </div>
              {showSlowStockDetails ? <ChevronUp className="text-amber-600" size={20} /> : <ChevronDown className="text-amber-600" size={20} />}
            </div>

            {showSlowStockDetails && (
              <div className="mt-4 border border-amber-200 rounded-lg overflow-x-auto bg-white" data-testid="slow-stock-alert-table">
                <table className="w-full text-sm">
                  <thead className="bg-amber-100/60">
                    <tr>
                      <th className="text-left p-3">Produto</th>
                      <th className="text-left p-3">NF Entrada</th>
                      <th className="text-left p-3">Data Entrada</th>
                      <th className="text-center p-3">Dias Parado</th>
                      <th className="text-center p-3">Estoque</th>
                      <th className="text-right p-3">Preço Atual</th>
                      <th className="text-center p-3">Baixar</th>
                      <th className="text-right p-3">Preço Sugerido</th>
                    </tr>
                  </thead>
                  <tbody>
                    {slowStockAlerts.map((a, idx) => (
                      <tr key={a.product_id} className="border-t hover:bg-amber-50/50" data-testid={`slow-stock-row-${idx}`}>
                        <td className="p-3">
                          <p className="font-medium text-slate-800">{a.product_name}</p>
                          {a.sku && <p className="text-xs text-slate-500">SKU: {a.sku}</p>}
                        </td>
                        <td className="p-3 text-slate-600">{a.purchase_number}</td>
                        <td className="p-3 text-slate-600">{formatDate(a.entry_date)}</td>
                        <td className="p-3 text-center">
                          <Badge className={a.days_stopped >= 365 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>
                            {a.days_stopped} dias
                          </Badge>
                        </td>
                        <td className="p-3 text-center text-slate-600">
                          {a.current_stock}/{a.quantity_purchased} un ({a.stock_pct.toFixed ? a.stock_pct.toFixed(0) : a.stock_pct}%)
                        </td>
                        <td className="p-3 text-right text-slate-600">{formatCurrency(a.price)}</td>
                        <td className="p-3 text-center">
                          <span className={`inline-flex items-center gap-1 font-semibold ${a.days_stopped >= 365 ? 'text-red-600' : 'text-amber-600'}`}>
                            <TrendingDown size={14} />
                            -{a.discount_pct}%
                          </span>
                        </td>
                        <td className="p-3 text-right font-bold text-emerald-600">{formatCurrency(a.suggested_price)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card className="border border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <Input
                placeholder="Buscar por Nº NF ou Fornecedor..."
                value={filterSearch}
                onChange={(e) => setFilterSearch(e.target.value)}
                className="pl-10"
                data-testid="search-purchases"
              />
            </div>
            <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
              <SelectTrigger className="w-full sm:w-48" data-testid="filter-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="pendente">Pendente</SelectItem>
                <SelectItem value="confirmada">Confirmada</SelectItem>
                <SelectItem value="cancelada">Cancelada</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2">
              <Calendar size={18} className="text-slate-400 shrink-0" />
              <Input
                type="date"
                value={filterStartDate}
                onChange={(e) => setFilterStartDate(e.target.value)}
                className="w-full sm:w-40"
                title="Data início"
                data-testid="filter-start-date"
              />
              <span className="text-slate-400 shrink-0">até</span>
              <Input
                type="date"
                value={filterEndDate}
                onChange={(e) => setFilterEndDate(e.target.value)}
                className="w-full sm:w-40"
                title="Data fim"
                data-testid="filter-end-date"
              />
            </div>
            {(filterSearch || filterStatus || filterStartDate || filterEndDate) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setFilterSearch(''); setFilterStatus(''); setFilterStartDate(''); setFilterEndDate(''); }}
                className="text-slate-500 hover:text-red-600 shrink-0"
                data-testid="clear-filters-btn"
              >
                <XCircle size={16} className="mr-1" />
                Limpar
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total NF de Entrada</p>
                <p className="text-2xl font-bold text-blue-600" data-testid="total-purchases-value">
                  {formatCurrency(purchases.reduce((sum, p) => sum + (p.status !== 'cancelada' ? (p.total || 0) : 0), 0))}
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <DollarSign className="text-blue-600" size={24} />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Entradas Confirmadas</p>
                <p className="text-2xl font-bold text-emerald-600">
                  {formatCurrency(purchases.filter(p => p.status === 'confirmada').reduce((sum, p) => sum + (p.total || 0), 0))}
                </p>
              </div>
              <div className="p-3 bg-emerald-100 rounded-lg">
                <CheckCircle className="text-emerald-600" size={24} />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Entradas Pendentes</p>
                <p className="text-2xl font-bold text-amber-600">
                  {formatCurrency(purchases.filter(p => p.status === 'pendente').reduce((sum, p) => sum + (p.total || 0), 0))}
                </p>
              </div>
              <div className="p-3 bg-amber-100 rounded-lg">
                <Package className="text-amber-600" size={24} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card className="border border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Truck size={20} className="text-blue-600" />
            Entradas de Mercadorias ({purchases.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin text-blue-600" size={32} />
            </div>
          ) : purchases.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Truck size={48} className="mx-auto mb-4 text-slate-300" />
              <p>Nenhuma entrada encontrada</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nº Entrada</TableHead>
                    <TableHead>NF</TableHead>
                    <TableHead>Fornecedor</TableHead>
                    <TableHead>Data Entrada</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {purchases.map((purchase) => (
                    <TableRow key={purchase.id} data-testid={`purchase-row-${purchase.id}`}>
                      <TableCell className="font-mono text-sm">{purchase.purchase_number}</TableCell>
                      <TableCell className="font-mono text-sm">{purchase.invoice_number}</TableCell>
                      <TableCell className="font-medium">{purchase.supplier_name}</TableCell>
                      <TableCell>{formatDate(purchase.entry_date)}</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(purchase.total)}</TableCell>
                      <TableCell>
                        <Badge className={statusColors[purchase.status]}>
                          {statusLabels[purchase.status]}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => viewPurchase(purchase)} className="text-slate-600 hover:text-blue-600">
                            <Eye size={16} />
                          </Button>
                          {purchase.status === 'pendente' && (
                            <>
                              <Button variant="ghost" size="sm" onClick={() => confirmPurchase(purchase.id)} className="text-emerald-600 hover:text-emerald-700">
                                <CheckCircle size={16} />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => cancelPurchase(purchase.id)} className="text-amber-600 hover:text-amber-700">
                                <XCircle size={16} />
                              </Button>
                            </>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => deletePurchase(purchase.id)} className="text-red-600 hover:text-red-700" title="Excluir entrada">
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Purchase Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalhes da Entrada {selectedPurchase?.purchase_number}</DialogTitle>
          </DialogHeader>
          {selectedPurchase && (
            <div className="space-y-6">
              {/* Header Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-xs text-slate-500">NF</p>
                  <p className="font-medium">{selectedPurchase.invoice_number} - Série {selectedPurchase.invoice_series || '1'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Fornecedor</p>
                  <p className="font-medium">{selectedPurchase.supplier_name}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">CNPJ</p>
                  <p className="font-medium font-mono">{selectedPurchase.supplier_cnpj || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Status</p>
                  <Badge className={statusColors[selectedPurchase.status]}>
                    {statusLabels[selectedPurchase.status]}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Data Emissão</p>
                  <p className="font-medium">{formatDate(selectedPurchase.invoice_date)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Data Entrada</p>
                  <p className="font-medium">{formatDate(selectedPurchase.entry_date)}</p>
                </div>
                <div className="md:col-span-2">
                  <p className="text-xs text-slate-500">Chave NF-e</p>
                  <p className="font-mono text-xs break-all">{selectedPurchase.invoice_key || '-'}</p>
                </div>
              </div>

              {/* Items */}
              <div>
                <h4 className="font-medium mb-2">Itens da Nota</h4>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Produto</TableHead>
                      <TableHead className="text-center">Qtd</TableHead>
                      <TableHead className="text-right">Unit.</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedPurchase.items?.map((item, i) => (
                      <TableRow key={i}>
                        <TableCell>{item.product_name}</TableCell>
                        <TableCell className="text-center">{item.quantity}</TableCell>
                        <TableCell className="text-right">{formatCurrency(item.unit_price)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(item.total)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Totals */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-xs text-slate-500">Subtotal</p>
                  <p className="font-medium">{formatCurrency(selectedPurchase.subtotal)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Desconto</p>
                  <p className="font-medium">{formatCurrency(selectedPurchase.discount)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Frete</p>
                  <p className="font-medium">{formatCurrency(selectedPurchase.shipping)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Total NF</p>
                  <p className="font-bold text-lg text-blue-600">{formatCurrency(selectedPurchase.total)}</p>
                </div>
              </div>

              {/* Taxes */}
              {(selectedPurchase.icms_total || selectedPurchase.ipi_total) && (
                <div className="grid grid-cols-4 gap-4 p-4 bg-amber-50 rounded-lg">
                  <div>
                    <p className="text-xs text-slate-500">ICMS</p>
                    <p className="font-medium">{formatCurrency(selectedPurchase.icms_total)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">IPI</p>
                    <p className="font-medium">{formatCurrency(selectedPurchase.ipi_total)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">PIS</p>
                    <p className="font-medium">{formatCurrency(selectedPurchase.pis_total)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">COFINS</p>
                    <p className="font-medium">{formatCurrency(selectedPurchase.cofins_total)}</p>
                  </div>
                </div>
              )}

              {selectedPurchase.notes && (
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500">Observações</p>
                  <p>{selectedPurchase.notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Purchases;
